import { useEffect, useRef, useState, useCallback } from "react";

const LANG_TO_BCP47 = {
  en: "en-US",
  hi: "hi-IN",
  mr: "mr-IN",
  ta: "ta-IN",
  te: "te-IN",
};

export const useVoiceInput = (language = "hi", { onResult } = {}) => {
  const [supported, setSupported] = useState(false);
  const [listening, setListening] = useState(false);
  const [error, setError] = useState("");
  const recRef = useRef(null);

  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    setSupported(!!SR);
    if (!SR) return;
    const rec = new SR();
    rec.continuous = false;
    rec.interimResults = false;
    rec.maxAlternatives = 1;
    rec.lang = LANG_TO_BCP47[language] || "hi-IN";
    rec.onresult = (e) => {
      const text = e.results?.[0]?.[0]?.transcript || "";
      if (text && onResult) onResult(text);
    };
    rec.onerror = (e) => {
      setError(e.error || "speech-error");
      setListening(false);
    };
    rec.onend = () => setListening(false);
    recRef.current = rec;
    return () => {
      try {
        rec.abort();
      } catch (err) {
        // recognition may already be stopped/aborted — log at debug level
        if (process.env.NODE_ENV !== "production") console.debug("voice: abort on cleanup", err);
      }
    };
    // eslint-disable-next-line
  }, [language]);

  const start = useCallback(() => {
    setError("");
    if (!recRef.current) return;
    try {
      recRef.current.start();
      setListening(true);
    } catch (e) {
      setError(e?.message || "start-failed");
    }
  }, []);

  const stop = useCallback(() => {
    try {
      recRef.current?.stop();
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.debug("voice: stop", err);
    }
    setListening(false);
  }, []);

  return { supported, listening, error, start, stop };
};
